import React from "react";
import {Col, ControlLabel, FormControl, FormGroup, HelpBlock, OverlayTrigger, Tooltip} from "react-bootstrap";
import {Alert, Button, Form, Modal} from "react-bootstrap/lib";
import {defineMessages, FormattedMessage, InjectedIntlProps} from "react-intl";
import {ChooseFolderDialog} from "../../settings/dataroots/ChooseFolderDialog";
import {DataRoot} from "../../settings/dataroots/model";
import {ErrorDisplay} from "./errordisplay/ErrorDisplay";
import {LcdIcon} from "./icon/LcdIcon";
import {Spinner} from "./spinner/Spinner";
import InputGroup from "react-bootstrap/lib/InputGroup";
import InputGroupAddon from "react-bootstrap/lib/InputGroupAddon";
import InputGroupButton from "react-bootstrap/lib/InputGroupButton";
import {mapErrorToMessage} from "../util/ErrorMapper";
import {APOLLO_CATALOG_ROOT_FOLDER, NewFileInfo} from "../../data/model";
import {WithApiProperties} from "../util/WithApi";

const LABEL_COL_WIDTH = 2;
const MESSAGES = defineMessages({
  unknownParent: {
    id: "studio.create-folder-dialog.unknown-parent",
    defaultMessage: "Unknown Parent",
  }
});

export interface AddButtonProps<T> {
  buttonTitle: string;
  confirmButtonTitle: string;
  modalTitle: string;
  addHelperText: string;
  chooseFileTitle: string;
  chooseFileButtonTitle: string;
  executingMessage: string;
  onAdd: (file: NewFileInfo) => Promise<T>;
  onAddSuccess: (data?: any) => void;
  parent?: string;
  disabled?: boolean;
  tooltip?: string;
}

interface ButtonState {
  isDialogOpen: boolean;
  isExecuting: boolean;
  isSuccess: boolean;
  error: Error;
  filePath: string;
  isChooseFileDialogOpen: boolean;
  parent: string;
  parentFolderName: string;
}

export function createAddButtonComponent<T>() {
  return class DeleteButtonComponent extends React.Component<AddButtonProps<T> & WithApiProperties & InjectedIntlProps, ButtonState> {

    constructor(props) {
      super(props);
      this.state = {
        isDialogOpen: false,
        isExecuting: false,
        error: null,
        isSuccess: false,
        filePath: "",
        isChooseFileDialogOpen: false,
        parent: "",
        parentFolderName: ""
      };
    }

    componentDidUpdate() {
      this.updateParentDisplayName();
    }

    updateParentDisplayName = () => {
      const {parent, api} = this.props;
      if (typeof this.props.parent === 'undefined' || this.state.parent === this.props.parent) {
        return
      }

      if (parent === null) {
        this.setState({
          parentFolderName: APOLLO_CATALOG_ROOT_FOLDER.title,
          parent
        });
      } else {
        api.getDataDetails(parent)
            .then((response) => {
              this.setState({
                parentFolderName: response.data.title,
                parent
              });
            })
            .catch((error) => {
              this.setState({
                error,
                parentFolderName: this.props.intl.formatMessage(MESSAGES.unknownParent),
                parent
              });
            });
      }
    }

    openDialog = () => {
      this.setState({
        isExecuting: false,
        error: null,
        isSuccess: false,
        isDialogOpen: true,
        isChooseFileDialogOpen: false,
      });
    }

    closeDialog = () => {
      this.setState({isDialogOpen: false});
    }

    showChooseFileDialog = () => {
      this.setState({isChooseFileDialogOpen: true});
    }

    closeChooseFileDialog = () => {
      this.setState({isChooseFileDialogOpen: false});
    }

    handleChooseFile = (dataRoot: DataRoot) => {
      this.setState({filePath: dataRoot.rootPath});
      return Promise.resolve();
    }

    performAdd = () => {
      this.setState({isExecuting: true, error: null, isSuccess: false});
      return this.props.onAdd({filePath: this.state.filePath, parent: this.props.parent}).then((data) => {
        this.props.onAddSuccess(data);
        this.setState({isExecuting: false, error: null, isSuccess: true});
        this.closeDialog();
      }).catch((error) => {
        this.setState({isExecuting: false, error, isSuccess: false});
      });
    }

    renderButton() {
      const {buttonTitle, disabled, tooltip} = this.props;
      if (tooltip) {
        return <OverlayTrigger placement="top"
                               overlay={<Tooltip id="tooltip_addData">{tooltip}</Tooltip>}>
          <span>
            <Button bsStyle="primary" className="actionBarItem"
                    onClick={() => {
                      this.openDialog();
                      (document.activeElement as HTMLElement).blur(); //make sure tooltip hides
                    }}
                    disabled={disabled ? disabled : false}>
             <LcdIcon icon="plus"/> {buttonTitle}
           </Button>
          </span>
        </OverlayTrigger>

      } else {
        return (<Button bsStyle="primary" className="actionBarItem" onClick={this.openDialog}
                        disabled={disabled ? disabled : false}>
          <LcdIcon icon="plus"/> {buttonTitle}
        </Button>);
      }
    }

    renderTitle() {
      const {isExecuting, isSuccess} = this.state;
      const {modalTitle} = this.props;

      if (isExecuting || isSuccess) {
        return null;
      }
      return <Modal.Title>{modalTitle}</Modal.Title>;
    }

    renderButtons() {
      const {isExecuting, isSuccess, filePath} = this.state;
      const {confirmButtonTitle} = this.props;

      if (isExecuting || isSuccess) {
        return null;
      }

      return (
          <div>
            <Button disabled={!filePath || filePath === ""} onClick={this.performAdd}> <LcdIcon
                icon="add"/> {confirmButtonTitle}
            </Button>
            <Button onClick={this.closeDialog}><FormattedMessage id="studio.ui.add-dialog.cancel"
                                                                 defaultMessage="Cancel"/></Button>
          </div>
      );
    }

    renderExecuting() {
      const {isExecuting} = this.state;
      const {executingMessage} = this.props;
      if (isExecuting) {
        return (
            <div style={{textAlign: "center"}}>
              <h2>{executingMessage}</h2>
              <Spinner bigStyle/>
            </div>
        );
      }
      return null;
    }

    renderError() {
      const {error} = this.state;

      if (error) {
        const errorMessage = mapErrorToMessage(error);
        if (errorMessage) {
          return <Alert bsStyle="danger">{errorMessage as React.ReactNode}</Alert>;
        }
        return <ErrorDisplay error={error}/>;
      }
      return null;
    }

    renderParent() {
      if (typeof this.props.parent !== 'undefined') {
        return <div>
          <strong><FormattedMessage
              id="studio.add-modal.selected-parent"
              defaultMessage="Selected Parent:"/> </strong>
          {this.state.parentFolderName}
        </div>
      }
    }

    renderBody() {
      const {isExecuting} = this.state;

      if (isExecuting) {
        return this.renderExecuting();
      }

      const {addHelperText} = this.props;
      const {filePath} = this.state;
      return (
          <div>
            {this.renderError()}
            <Form horizontal>
              {this.renderParent()}
              <br/>
              <FormGroup>
                <Col sm={LABEL_COL_WIDTH}>
                  <ControlLabel>
                    <FormattedMessage
                        id="studio.add-modal.filePath"
                        defaultMessage="File path"
                    />
                  </ControlLabel>
                </Col>
                <Col sm={12 - LABEL_COL_WIDTH}>
                  <InputGroup>
                    <FormControl className={"browse-data-input"} type="text"
                                 value={filePath} onChange={(event) => {
                      const newValue = (event.target as any).value;
                      this.setState({filePath: newValue});
                    }}/>
                    <InputGroupAddon className={"browse-data-button"}
                                     onClick={this.showChooseFileDialog}>
                      <InputGroupButton>
                        <LcdIcon icon="folder-open"/>
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>
                </Col>
                <Col smOffset={LABEL_COL_WIDTH} sm={12 - LABEL_COL_WIDTH}>
                  <HelpBlock>
                    {addHelperText}
                  </HelpBlock>
                </Col>
              </FormGroup>
            </Form>
          </div>
      );
    }

    renderModal() {
      const {isDialogOpen, isExecuting} = this.state;
      return (
          <Modal show={isDialogOpen} onHide={this.closeDialog} backdrop="static">
            <Modal.Header closeButton={!isExecuting}>
              {this.renderTitle()}
            </Modal.Header>
            <Modal.Body id={"add-modal-body"}>
              {this.renderBody()}
            </Modal.Body>
            <Modal.Footer>
              {this.renderButtons()}
            </Modal.Footer>
          </Modal>
      );
    }

    renderFileChooser() {
      const {chooseFileTitle, chooseFileButtonTitle} = this.props;
      const {isChooseFileDialogOpen} = this.state;
      return (
          <ChooseFolderDialog handleChoose={this.handleChooseFile}
                              show={isChooseFileDialogOpen}
                              onHide={this.closeChooseFileDialog}
                              onFolderChoosed={this.closeChooseFileDialog}
                              title={chooseFileTitle}
                              buttonText={chooseFileButtonTitle}
          />
      );
    }

    render() {
      return (
          <div>
            {this.renderButton()}
            {this.renderModal()}
            {this.renderFileChooser()}
          </div>
      );
    }
  };
}